import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import { pageTypeConstants } from '../../constants/pageTypeConstants';
import tracking from 'dibs-tracking';

import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespRefineMenuSelector } from '../SbRespRefineMenuSelector/SbRespRefineMenuSelector';

import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SharedPagination } from '../../shared/SharedPagination/SharedPagination';
import { SbRespRefineTopNav } from '../SbRespRefineTopNav/SbRespRefineTopNav';
import { SharedBottomModule } from '../../shared/SharedBottomModule/SharedBottomModule';

import { VERTICALS } from '../../constants/uriConstants';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

import styles from './SbRespMainContent.scss';

function SbRespMainContentComponent({
    currency,
    itemSearch,
    viewer,
    user,
    isClient,
    isThinPage,
    isThinBrowsePage,
    relay,
    generatedUriRef,
    showMeasurements,
    page,
}) {
    const {
        topCategoryL1,
        displayUriRef,
        displayMaxNumberOfPages,
        pageType,
        bottomModule,
    } = itemSearch;

    const { canShowInternalSellerName, canShowSellerName } = user || {};

    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;
    const isExclusivePage = pageType === pageTypes.EXCLUSIVE;

    const sharedProps = {
        currency,
        itemSearch,
        viewer,
        user,
        isClient,
    };

    const { isSbTopNavVariant } = useContext(ServerVarsContext);
    useEffect(() => {
        const trackingData = getAbTestTrackingData(document.cookie, 'SB_TOP_FILTER_TEST');
        tracking.trackAbTestEvent(trackingData, pageTypeConstants.BROWSE);
    }, []);

    return (
        <div
            className={classnames(styles.mainContentContainer, {
                isSbTopNavVariant,
            })}
        >
            {!isSbTopNavVariant && (
                <SbRespRefineMenuSelector
                    {...sharedProps}
                    uriRef={generatedUriRef}
                    isThinPage={isThinPage}
                />
            )}
            <div className={styles.resultsContainer}>
                {isSbTopNavVariant ? (
                    <SbRespRefineTopNav
                        itemSearch={itemSearch}
                        viewer={viewer}
                        user={user}
                        currency={currency}
                        isClient={isClient}
                    />
                ) : (
                    <div className={styles.resultsHeader}>
                        <div className={styles.sortContainer}>
                            <SbRespSortItems itemSearch={itemSearch} />
                            <SbRespPageSize itemSearch={itemSearch} />
                        </div>
                        <SbRespBrowseHeader itemSearch={itemSearch} />
                    </div>
                )}

                <div className={styles.resultsContainer}>
                    {/* Item Results */}
                    <SbRespSearchResultContainer
                        {...sharedProps}
                        showInternalSellerName={canShowInternalSellerName}
                        showSellerName={!!canShowSellerName}
                        showCreatorName={canShowCreatorName}
                        showMeasurements={showMeasurements}
                        relay={relay}
                    />
                    {/* Related Items */}
                    {isThinBrowsePage && !isExclusivePage && (
                        <SbSharedRelatedItems
                            itemSearch={itemSearch}
                            user={user}
                            currency={currency}
                            isClient={isClient}
                            viewer={viewer}
                        />
                    )}
                    {/* Pagination */}
                    <div className={styles.paginationContainer}>
                        <SharedPagination
                            currentPage={page}
                            numberOfPages={displayMaxNumberOfPages}
                            displayUriRef={displayUriRef}
                        />
                    </div>

                    <SharedBottomModule
                        className={styles.bottomModule}
                        bottomModule={bottomModule}
                    />
                </div>
            </div>
        </div>
    );
}

SbRespMainContentComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.shape({
        displayUriRef: PropTypes.string.isRequired,
        topCategoryL1: PropTypes.string,
        displayMaxNumberOfPages: PropTypes.number,
        pageType: PropTypes.string,
        bottomModule: PropTypes.array,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    isThinPage: PropTypes.bool.isRequired,
    isThinBrowsePage: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    generatedUriRef: PropTypes.string,
    showMeasurements: PropTypes.bool,
    page: PropTypes.number,
};

const mapStateToProps = ({ displayPreferences, filters }) => ({
    showMeasurements: displayPreferences.showMeasurements,
    generatedUriRef: filters.generatedUriRef,
    page: filters.page,
});

export const SbRespMainContent = createFragmentContainer(
    connect(mapStateToProps)(SbRespMainContentComponent),
    {
        itemSearch: graphql`
            fragment SbRespMainContent_itemSearch on ItemSearchQueryConnection {
                topCategoryL1
                displayUriRef
                pageType
                displayMaxNumberOfPages
                bottomModule {
                    ...SharedBottomModule_bottomModule
                }
                ...SbRespRefineMenuSelector_itemSearch
                ...SbRespSortItems_itemSearch
                ...SbRespPageSize_itemSearch
                ...SbRespBrowseHeader_itemSearch
                ...SbRespSearchResultContainer_itemSearch
                ...SbSharedRelatedItems_itemSearch
                ...SbRespRefineTopNav_itemSearch
            }
        `,
        user: graphql`
            fragment SbRespMainContent_user on User {
                canShowSellerName
                canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
                ...SbRespRefineMenuSelector_user
                ...SbRespSearchResultContainer_user
                ...SbSharedRelatedItems_user
                ...SbRespRefineTopNav_user
            }
        `,
        viewer: graphql`
            fragment SbRespMainContent_viewer on Viewer {
                ...SbRespRefineMenuSelector_viewer
                ...SbRespSearchResultContainer_viewer
                ...SbSharedRelatedItems_viewer
                ...SbRespRefineTopNav_viewer
            }
        `,
    }
);
